import {
  Box,
  Dialog,
  IconButton,
  Typography,
  makeStyles,
} from "@material-ui/core";
import CloseIcon from "@mui/icons-material/Close";
import themeColorsDesingSystem from "assets/theme/ds-colors";
import { formatNumber } from "./utils";

const useStyles = makeStyles({
  backdrop: {
    backgroundColor: "rgba(15, 23, 42, 0.6)",
    backdropFilter: "blur(4px)",
  },
  dialogPaper: {
    borderRadius: 8,
    maxWidth: 960,
    width: "100%",
    maxHeight: "85vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    margin: 16,
  },
  header: {
    padding: "1.25rem 1.5rem",
    borderBottom: `1px solid ${themeColorsDesingSystem.stone.main}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
  },
  title: {
    fontSize: "1.125rem",
    fontWeight: 700,
    color: themeColorsDesingSystem.primary.main,
  },
  body: {
    flex: 1,
    overflow: "auto",
    padding: "1.25rem 1.5rem",
    backgroundColor: themeColorsDesingSystem.background.main,
  },
  userCard: {
    border: `1px solid ${themeColorsDesingSystem.stone.main}`,
    borderRadius: 8,
    marginBottom: "1rem",
    overflow: "hidden",
  },
  userCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "0.875rem 1rem",
    backgroundColor: themeColorsDesingSystem.stone.light,
    borderBottom: `1px solid ${themeColorsDesingSystem.stone.main}`,
  },
  userName: {
    fontSize: "0.875rem",
    fontWeight: 700,
    color: themeColorsDesingSystem.gray.dark,
  },
  userTotal: {
    fontSize: "0.8125rem",
    fontWeight: 600,
    color: themeColorsDesingSystem.primary.main,
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "0.625rem 1rem",
    borderBottom: `1px solid ${themeColorsDesingSystem.stone.light}`,
    "&:last-child": {
      borderBottom: "none",
    },
  },
  detailLabel: {
    fontSize: "0.8125rem",
    color: themeColorsDesingSystem.gray.dark,
    paddingRight: "1rem",
  },
  detailValue: {
    fontSize: "0.8125rem",
    fontWeight: 700,
    color: themeColorsDesingSystem.text.primary,
    flexShrink: 0,
  },
  featureCard: {
    border: `1px solid ${themeColorsDesingSystem.stone.main}`,
    borderRadius: 8,
    marginBottom: "1rem",
    padding: "1rem",
  },
  featureTitle: {
    fontSize: "0.875rem",
    fontWeight: 700,
    color: themeColorsDesingSystem.gray.dark,
    marginBottom: "0.75rem",
  },
  featureMeta: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: "0.8125rem",
    color: themeColorsDesingSystem.gray.main,
    marginBottom: "0.375rem",
  },
  emptyState: {
    fontSize: "0.875rem",
    color: themeColorsDesingSystem.gray.main,
    textAlign: "center",
    padding: "2rem 1rem",
  },
  subEmpty: {
    fontSize: "0.8125rem",
    color: themeColorsDesingSystem.gray.main,
    padding: "0.75rem 1rem",
  },
});

function UsersDetailsContent({ items }) {
  const classes = useStyles();

  if (!items?.length) {
    return (
      <Typography className={classes.emptyState}>
        Nenhum detalhe de usuário disponível para o período.
      </Typography>
    );
  }

  return items.map((item, index) => (
    <Box key={`${item.usuario}-${index}`} className={classes.userCard}>
      <Box className={classes.userCardHeader}>
        <Typography className={classes.userName}>{item.usuario}</Typography>
        <Typography className={classes.userTotal}>
          {formatNumber(item.quantidadeAcesso)} acessos
        </Typography>
      </Box>
      {item.funcionalidades?.length ? (
        item.funcionalidades.map((funcionalidade, funcIndex) => (
          <Box key={`${funcionalidade.nome}-${funcIndex}`} className={classes.detailRow}>
            <Typography className={classes.detailLabel}>
              {funcionalidade.nome}
            </Typography>
            <Typography className={classes.detailValue}>
              {formatNumber(funcionalidade.quantidade)}
            </Typography>
          </Box>
        ))
      ) : (
        <Typography className={classes.subEmpty}>
          Sem funcionalidades registradas para este usuário.
        </Typography>
      )}
    </Box>
  ));
}

function FeaturesDetailsContent({ items }) {
  const classes = useStyles();

  if (!items?.length) {
    return (
      <Typography className={classes.emptyState}>
        Nenhum detalhe de funcionalidade disponível para o período.
      </Typography>
    );
  }

  return items.map((item, index) => (
    <Box key={`${item.funcionalidade}-${index}`} className={classes.featureCard}>
      <Typography className={classes.featureTitle}>{item.funcionalidade}</Typography>
      <Box className={classes.featureMeta}>
        <span>Total de acessos</span>
        <strong>{formatNumber(item.quantidadeAcesso)}</strong>
      </Box>
      <Box className={classes.featureMeta}>
        <span>Usuário que mais acessou</span>
        <strong>{item.usuarioMaisAtivo}</strong>
      </Box>
      <Box className={classes.featureMeta}>
        <span>Acessos do usuário</span>
        <strong>{formatNumber(item.quantidadeUsuarioMaisAtivo)}</strong>
      </Box>
    </Box>
  ));
}

export default function TopChartDetailsModal({
  open,
  onClose,
  title,
  variant,
  usersDetails = [],
  featuresDetails = [],
}) {
  const classes = useStyles();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
      BackdropProps={{ className: classes.backdrop }}
    >
      <Box className={classes.header}>
        <Typography className={classes.title}>{title}</Typography>
        <IconButton size="small" onClick={onClose} aria-label="Fechar">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box className={classes.body}>
        {variant === "users" ? (
          <UsersDetailsContent items={usersDetails} />
        ) : (
          <FeaturesDetailsContent items={featuresDetails} />
        )}
      </Box>
    </Dialog>
  );
}
